/** Draw order and culling. Pure; `test/world.spec.ts` covers both.
 *
 * Everything that stands on the map - objects, towns, heroes, boats - is sorted
 * by its GROUND LINE, so a nearer object overlaps a farther one. A renderer
 * that sorts by sprite top, or by insertion order, produces a map where a tree
 * one row south is drawn behind the castle it should be standing in front of;
 * that single mistake is what makes a 2D strategy map read as flat.
 */

import type { PixelRect } from "./frames.js";
import { rectsIntersect } from "./frames.js";

export interface Drawable {
  /** Stable identity; the last sort key, so the order is total and replayable. */
  readonly id: string;
  /** World pixel of the ground line the sprite stands on. */
  readonly groundX: number;
  readonly groundY: number;
  /** Drawn bounds in world pixels, used for culling. */
  readonly bounds: PixelRect;
  /**
   * Ties inside a cell: a hero standing on an object draws in front of it, a
   * flat decal behind it. Higher sorts later, i.e. nearer the viewer.
   */
  readonly layer: number;
}

/**
 * Total order: ground line, then x, then layer, then id.
 *
 * `id` last is what makes the order independent of the iteration order of the
 * observation, so two clients handed the same `PlayerObservation` draw the same
 * frame.
 */
export function compareGroundLine(a: Drawable, b: Drawable): number {
  if (a.groundY !== b.groundY) return a.groundY - b.groundY;
  if (a.layer !== b.layer) return a.layer - b.layer;
  if (a.groundX !== b.groundX) return a.groundX - b.groundX;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function sortByGroundLine<T extends Drawable>(items: readonly T[]): T[] {
  return [...items].sort(compareGroundLine);
}

/**
 * Cull to the camera rect grown by one tile.
 *
 * The grow is not enough on its own for tall sprites - a 6-tile castle anchored
 * well below the viewport still reaches into it - so the test is against the
 * sprite's own drawn bounds, which `frameBounds` already computed from the
 * anchor. `pad` only covers the sub-pixel and animation-offset cases.
 */
export function cullToRect<T extends Drawable>(
  items: readonly T[], view: PixelRect, pad: number,
): T[] {
  const grown: PixelRect = {
    x: view.x - pad, y: view.y - pad, w: view.w + pad * 2, h: view.h + pad * 2,
  };
  const out: T[] = [];
  for (const it of items) if (rectsIntersect(it.bounds, grown)) out.push(it);
  return out;
}
